/**
 * Утилита для управления токенами доступа
 * Обеспечивает централизованную работу с токенами во всем приложении
 */

class TokenManager {
  constructor() {
    this.refreshPromise = null;
    this.refreshThreshold = 300; // 5 минут до истечения
    this.listeners = new Set();
    this.authContext = null; // Ссылка на контекст авторизации
    this.currentToken = null; // Текущий токен
  }

  /**
   * Добавляет слушатель событий токена
   * @param {Function} listener - функция обратного вызова
   */
  addListener(listener) {
    this.listeners.add(listener);
  }

  /**
   * Удаляет слушатель событий токена
   * @param {Function} listener - функция обратного вызова
   */
  removeListener(listener) {
    this.listeners.delete(listener);
  }

  /**
   * Уведомляет всех слушателей о событии
   * @param {string} event - тип события
   * @param {any} data - данные события
   */
  notifyListeners(event, data) {
    // Обновляем текущий токен при получении нового
    if (event === 'TOKEN_REFRESHED' && data && data.access_token) {
      this.setCurrentToken(data.access_token);
    } else if (event === 'TOKEN_CLEARED') {
      this.setCurrentToken(null);
    }
    
    this.listeners.forEach(listener => {
      try {
        listener(event, data);
      } catch (error) {
        console.error('❌ Ошибка в слушателе токена:', error);
      }
    });
  }

  /**
   * Проверяет, действителен ли токен
   * @param {Object} user - объект пользователя с токеном
   * @returns {boolean}
   */
  isTokenValid(user) {
    if (!user || !user.access_token || !user.expires_at) {
      return false;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return currentTime < user.expires_at;
  }

  /**
   * Проверяет, нужно ли обновить токен
   * @param {Object} user - объект пользователя с токеном
   * @returns {boolean}
   */
  shouldRefreshToken(user) {
    if (!user || !user.expires_at) {
      return false;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return (user.expires_at - currentTime) < this.refreshThreshold;
  }

  /**
   * Получает время до истечения токена в секундах
   * @param {Object} user - объект пользователя с токеном
   * @returns {number}
   */
  getTimeToExpiry(user) {
    if (!user || !user.expires_at) {
      return 0;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return Math.max(0, user.expires_at - currentTime);
  }

  /**
   * Форматирует время истечения токена
   * @param {Object} user - объект пользователя с токеном
   * @returns {string}
   */
  formatExpiryTime(user) {
    if (!user || !user.expires_at) {
      return 'Неизвестно';
    }

    const expiryDate = new Date(user.expires_at * 1000);
    return expiryDate.toLocaleString('ru-RU');
  }

  /**
   * Обновляет токен с защитой от множественных запросов
   * @param {Object} auth - объект авторизации из react-oidc-context
   * @returns {Promise<boolean>}
   */
  async refreshToken(auth) {
    // Если уже идет процесс обновления, ждем его завершения
    if (this.refreshPromise) {
      try {
        await this.refreshPromise;
        return true;
      } catch (error) {
        return false;
      }
    }

    // Создаем новый промис обновления
    this.refreshPromise = this._performRefresh(auth);

    try {
      await this.refreshPromise;
      this.notifyListeners('TOKEN_REFRESHED', auth.user);
      return true;
    } catch (error) {
      this.notifyListeners('TOKEN_REFRESH_FAILED', error);
      return false;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Выполняет фактическое обновление токена
   * @param {Object} auth - объект авторизации
   * @returns {Promise}
   * @private
   */
  async _performRefresh(auth) {
    console.log('🔄 Начинаем обновление токена...');

    try {
      // Пытаемся выполнить silent refresh
      await auth.signinSilent();
      console.log('✅ Токен успешно обновлен через silent refresh');
      return;
    } catch (silentError) {
      console.warn('⚠️ Silent refresh не удался:', silentError.message);

      // Если есть refresh token, пытаемся использовать его
      if (auth.user?.refresh_token) {
        try {
          console.log('🔄 Попытка обновления через refresh token...');
          
          // Получаем UserManager
          const userManager = auth.settings.userManager || auth._userManager;
          if (userManager) {
            await userManager.signinSilent();
            console.log('✅ Токен обновлен через refresh token');
            return;
          }
        } catch (refreshError) {
          console.error('❌ Ошибка при обновлении через refresh token:', refreshError);
        }
      }

      // Если все попытки не удались, выбрасываем ошибку
      throw new Error('Не удалось обновить токен');
    }
  }

  /**
   * Выполняет API запрос с автоматическим обновлением токена при необходимости
   * @param {Object} auth - объект авторизации
   * @param {Function} apiCall - функция API запроса, принимающая токен
   * @param {number} retries - количество попыток (по умолчанию 1)
   * @returns {Promise<any>}
   */
  async executeWithValidToken(auth, apiCall, retries = 1) {
    if (!auth.isAuthenticated || !auth.user) {
      throw new Error('Пользователь не авторизован');
    }

    // Проверяем, нужно ли обновить токен
    if (this.shouldRefreshToken(auth.user)) {
      console.log('⏰ Токен скоро истечет, обновляем...');
      const refreshed = await this.refreshToken(auth);
      if (!refreshed) {
        throw new Error('Не удалось обновить токен');
      }
    }

    // Проверяем валидность токена
    if (!this.isTokenValid(auth.user)) {
      throw new Error('Токен недействителен');
    }

    try {
      return await apiCall(auth.user.access_token);
    } catch (error) {
      // Если ошибка авторизации и есть попытки, пытаемся обновить токен
      if ((error.response?.status === 401 || error.response?.status === 403) && retries > 0) {
        console.log('🔄 Ошибка авторизации, пытаемся обновить токен и повторить запрос...');
        
        const refreshed = await this.refreshToken(auth);
        if (refreshed && this.isTokenValid(auth.user)) {
          return await this.executeWithValidToken(auth, apiCall, retries - 1);
        }
      }
      
      throw error;
    }
  }

  /**
   * Очищает данные токена
   */
  clearTokenData() {
    this.refreshPromise = null;
    this.notifyListeners('TOKEN_CLEARED', null);
  }

  /**
   * Получает информацию о состоянии токена
   * @param {Object} user - объект пользователя
   * @returns {Object}
   */
  getTokenInfo(user) {
    if (!user) {
      return {
        isValid: false,
        shouldRefresh: false,
        timeToExpiry: 0,
        expiryTime: 'Неизвестно',
        status: 'NO_TOKEN'
      };
    }

    const isValid = this.isTokenValid(user);
    const shouldRefresh = this.shouldRefreshToken(user);
    const timeToExpiry = this.getTimeToExpiry(user);
    const expiryTime = this.formatExpiryTime(user);

    let status = 'VALID';
    if (!isValid) {
      status = 'EXPIRED';
    } else if (shouldRefresh) {
      status = 'NEEDS_REFRESH';
    }

    return {
      isValid,
      shouldRefresh,
      timeToExpiry,
      expiryTime,
      status
    };
  }

  /**
   * Устанавливает контекст авторизации
   * @param {Object} auth - объект авторизации из react-oidc-context
   */
  setAuthContext(auth) {
    this.authContext = auth;
  }

  /**
   * Получает контекст авторизации
   * @returns {Object|null}
   */
  getAuthContext() {
    return this.authContext;
  }

  /**
   * Устанавливает текущий токен
   * @param {string} token - токен доступа
   */
  setCurrentToken(token) {
    this.currentToken = token;
  }

  /**
   * Получает текущий токен
   * @returns {string|null}
   */
  getCurrentToken() {
    // Сначала проверяем сохраненный токен
    if (this.currentToken) {
      return this.currentToken;
    }
    
    // Если нет сохраненного токена, пытаемся получить из auth контекста
    if (this.authContext && this.authContext.user && this.authContext.user.access_token) {
      const token = this.authContext.user.access_token;
      this.setCurrentToken(token);
      return token;
    }
    
    return null;
  }
}

// Создаем единственный экземпляр
const tokenManager = new TokenManager();

export default tokenManager; 